import { motion } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

const reveal = (delay = 0) => ({
  initial: { y: 48, opacity: 0 },
  whileInView: { y: 0, opacity: 1 },
  viewport: { once: true, margin: "-10% 0px" },
  transition: { duration: 0.9, ease: EASE, delay },
});

const PILLARS = [
  {
    index: "01",
    title: "Scientific curiosity",
    text: "We ask the ocean questions before we answer for it — field investigation and continuous learning sit upstream of every model.",
  },
  {
    index: "02",
    title: "Engineering precision",
    text: "Solutions are built to be built: practical, reliable and future-ready, quantified to the standard regulators expect.",
  },
  {
    index: "03",
    title: "Environmental responsibility",
    text: "Development needs and ecological integrity are weighed together — never traded off silently.",
  },
];

export default function AboutSection() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative px-6 py-32 text-[#02252e] md:px-12 md:py-44"
    >
      <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-8">
        <div className="md:col-span-9">
          <motion.p
            {...reveal(0)}
            className="font-mono text-[11px] uppercase tracking-[0.35em] text-[#B34A00]"
            data-testid="about-overline"
          >
            About Okeanos
          </motion.p>
          <motion.h2
            {...reveal(0.08)}
            className="mt-6 font-display text-4xl font-light leading-[1.05] tracking-tighter md:text-6xl"
          >
            Shaped by coasts, driven by science,{" "}
            <span className="font-editorial italic">inspired by nature.</span>
          </motion.h2>
          <motion.p
            {...reveal(0.16)}
            className="mt-8 max-w-xl text-sm leading-relaxed text-[#083843]/85 md:text-base"
          >
            OKEANOS is a multi-disciplinary consulting company built by a
            group of passionate coastal, marine and environmental engineers
            who believe that innovation should never come at the cost of
            nature.
          </motion.p>
        </div>
      </div>

      <div className="mt-24 grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-8">
        <motion.p
          {...reveal(0)}
          className="font-mono text-[11px] uppercase tracking-[0.35em] text-[#06414d] md:col-span-2"
          data-testid="about-story-label"
        >
          Our story
        </motion.p>
        <div className="md:col-span-6">
          <motion.p
            {...reveal(0.05)}
            className="text-sm leading-relaxed text-[#083843]/85 md:text-base"
          >
            What began as a small team of energetic problem-solvers has grown
            into a trusted partner for complex coastal and marine projects
            across the Middle East, India and Vietnam.
          </motion.p>
          <motion.p
            {...reveal(0.12)}
            className="mt-5 text-sm leading-relaxed text-[#083843]/85 md:text-base"
          >
            Our strength lies in the blend of scientific curiosity,
            engineering precision and environmental responsibility. We hustle,
            we create, and we deliver — while safeguarding the delicate
            balance of coastal ecosystems.
          </motion.p>
        </div>
      </div>

      <div className="mt-28 grid grid-cols-1 gap-12 md:grid-cols-3 md:gap-10">
        {PILLARS.map((p, i) => (
          <motion.div
            key={p.index}
            {...reveal(i * 0.08)}
            data-testid={`about-pillar-${p.index}`}
          >
            <span className="pillar-index">{p.index}</span>
            <h3 className="mt-4 font-display text-xl font-light md:text-2xl">
              {p.title}
            </h3>
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-[#083843]/75">
              {p.text}
            </p>
            <span className="mt-5 block h-px w-12 bg-[#f58220]" />
          </motion.div>
        ))}
      </div>

      <motion.blockquote
        {...reveal(0.1)}
        data-testid="about-quote"
        className="mt-28 max-w-3xl font-editorial text-2xl italic leading-snug text-[#053540] md:text-4xl"
      >
        “Where the coast meets the ocean, passion becomes purpose — and nature
        becomes our greatest partner.”
      </motion.blockquote>

      <div className="mt-28 grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-8">
        <motion.p
          {...reveal(0)}
          className="font-mono text-[11px] uppercase tracking-[0.35em] text-[#9BE5E8] md:col-span-2"
          data-testid="about-who-label"
        >
          Who we are
        </motion.p>
        <div className="md:col-span-7">
          <motion.p
            {...reveal(0.05)}
            className="text-sm leading-relaxed text-[#EAF7F6]/90 md:text-base"
          >
            A handpicked team of scientists, engineers and environmental
            specialists — many with advanced degrees — working together to
            decode coastal processes, understand oceanographic systems, and
            design solutions that respect both development needs and
            ecological integrity.
          </motion.p>
          <motion.p
            {...reveal(0.12)}
            className="mt-5 text-sm leading-relaxed text-[#EAF7F6]/90 md:text-base"
          >
            We combine intensive field investigations with sophisticated
            numerical modelling tools to provide practical, reliable and
            future-ready solutions. Our commitment to continuous learning and
            technological advancement ensures that clients always receive the
            most recent and relevant expertise.
          </motion.p>
          <motion.p
            {...reveal(0.2)}
            className="mt-10 font-mono text-[10px] uppercase tracking-[0.25em] text-[#9BE5E8]/80"
            data-testid="about-strip"
          >
            Middle East — India — Vietnam{" "}
            <span className="text-[#f58220]">·</span> Advanced degrees{" "}
            <span className="text-[#f58220]">·</span> Field investigation +
            Numerical modelling
          </motion.p>
        </div>
      </div>
    </section>
  );
}
